/**
 * ASSISTANT — LES DEUX ÉTAPES QUI REGARDENT LA NUIT ENTIÈRE.
 *
 * D'abord LES ÉCARTS, ensuite LA SYNTHÈSE puis la finalisation. Les deux
 * partagent exactement le même matériau — les totaux de la nuit, ses points de
 * vente, ses écarts — et les séparer en deux fichiers aurait fait diverger
 * deux lectures du même chiffre.
 *
 * ═══ LES TROIS MONTANTS NE FUSIONNENT JAMAIS ═══
 * SOURCE, ce que le fournisseur a annoncé. CONSTATÉ, ce que le terrain a
 * compté — le comptage d'espèces et les TPE sont des constatés, pas des
 * sources. RETENU, ce que la direction financière arrête. Trois colonnes
 * distinctes du début à la fin : c'est toute la doctrine du domaine qui
 * tiendrait dans leur confusion.
 *
 * ═══ UN ÉCART N'EST PAS UNE FAUTE ═══
 * C'est un fait comptable à expliquer. Rien ici ne le rattache à une personne,
 * ne l'agrège par salarié ni ne le transforme en note. Et un terminal circule
 * d'une caisse à l'autre au cours d'une nuit : un montant carte qui ne tombe
 * pas sur un TPE donné n'est pas une anomalie de caisse, c'est le
 * fonctionnement normal — le rapprochement se lit au point de vente, au lieu
 * ou à la nuit, jamais terminal contre caisse.
 *
 * ═══ LE TOTAL DE LA NUIT EN TÊTE DE LA SYNTHÈSE ═══
 * La synthèse s'ouvre sur la feuille de caisse de la nuit (`FeuilleDeCaisse`,
 * le modèle du classeur de l'exploitante) : compté contre rapport par point
 * de vente, sous-totaux Bars / Service / Accueil, ÉCART TOTAL, chiffre
 * d'affaires (sur place + préventes) et récap des espèces. Elle remplace
 * l'ancien triptyque, dont la « source » comptait les offerts. Le RETENU suit,
 * dans « Ce qui sera arrêté » ; la finalisation, les écarts et la révision
 * n'ont pas bougé.
 *
 * ═══ FINALISER ═══
 * La finalisation est transactionnelle côté serveur : il n'existe jamais une
 * nuit à moitié close. Elle exige une ré-authentification forte — portée par
 * `adminMutate`, qui rejoue la mutation après le step-up plutôt que de perdre
 * le geste. Ensuite la clôture est IMMUABLE : la corriger crée une révision
 * qui laisse la précédente intacte et reconstructible.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react'
import {
  AlertTriangle, ArrowDown, CheckCircle2, Lock, RotateCcw, Scale, Users, X,
} from 'lucide-react'
import { Modal, Select, adminMutate, useToast } from '../admin/primitives.js'
import { useConfirm } from '../shell/confirm.js'
import { redirect } from '../router.js'
import { formatEcart, formatMoney } from './money.js'
import {
  DISCREPANCY_STATUS_LABELS, DISCREPANCY_STATUS_PILL, Ecart, EcartSeuil, MoneyInput, MotifField,
  Montant, motifSuffisant, enCentimes,
  ilYA, nuitLongue,
  type ClosingApi, type ClosingPosView, type DiscrepancyReasonRow, type DiscrepancyStatus,
  type DiscrepancyView, type Etat,
} from './ClosingCommon.js'
import { VoirSource, type DocVisible } from './SourceViewer.js'
import { ListeEntrees, type EntreesNuit } from './entrees.js'
import { BilletterieAssociations } from './BilletterieAssociations.js'
import type { NightDetailResponse } from './nights-common.js'
import { FeuilleDeCaisse, useNuitFinance } from './FeuilleDeCaisse.js'
import { TraceRemplacement, ValidationRemplacee } from './ValidationRemplacee.js'
import { feuilleDeCaisse, type NuitAvecPreventes } from './night-sheet-model.js'
import { niveauEcart, rapprochementsDeLaNuit, type RapprochementEcart } from './closing-rail-model.js'
import { ANNONCE_FINALISEE, sceller } from './sceau-cloture.js'
import { chiffreOfficiel, recapFinalisation, ventilationDuChiffre } from './chiffre-officiel.js'

/* ═══════════════════════════ ÉTAPE « ÉCARTS » ═══════════════════════════ */

/** Ce que rend une écriture d'écart. `version` chaîne les deux appels. */
interface ReponseEcriture { error?: string; message?: string; id?: string; version?: number }

/**
 * Une ligne de rapprochement : un périmètre, ses trois montants, son écart —
 * les chiffres de la FEUILLE (`rapprochementsDeLaNuit`, closing-rail-model),
 * et l'écart déjà enregistré qui s'y rattache.
 */
interface Rapprochement extends RapprochementEcart {
  ecart: DiscrepancyView | null
}

/**
 * L'ÉTAPE QUI MANQUAIT : EXPLIQUER, ACCEPTER, CORRIGER.
 *
 * Jusqu'ici la nuit affichait ses écarts sans jamais permettre d'y répondre —
 * et un écart qu'on ne peut pas expliquer là où on le découvre est un écart
 * qu'on retrouve deux ans plus tard sans savoir quoi en dire.
 *
 * LE RAPPROCHEMENT SE LIT AU POINT DE VENTE. C'est le niveau auquel le domaine
 * rattache un écart, et c'est aussi celui auquel la maison compte depuis
 * toujours. Descendre au terminal produirait des « écarts » qui n'en sont pas :
 * un TPE sert plusieurs caisses au cours d'une nuit.
 */
export function ClosingStepEcarts(props: {
  api: ClosingApi
  motifs: DiscrepancyReasonRow[]
  figee: boolean
  onEtape: (cle: string) => void
}) {
  const { api, motifs, figee, onEtape } = props
  const d = api.closing
  const toast = useToast()
  const seuils = { warn: api.settings.discrepancy_warn_cents, alert: api.settings.discrepancy_alert_cents }
  const peutQualifier = api.peut('finance.discrepancy.manage') && !figee
  const peutRetenir = api.peut('finance.amount.override') && !figee

  const [occupe, setOccupe] = useState(false)
  const sansReponse = d.pos.filter((p) => p.was_open === null).length

  /* ─── ce que les chiffres montrent, point de vente par point de vente ───
     Les chiffres de la feuille de caisse, hors offerts, comptage compris :
     le delta est ce que « Expliquer l'écart » préremplit PUIS enregistre.
     L'ancienne règle comptait les offerts dans la source, ignorait les
     espèces comptées non reportées et chiffrait un point de vente à moitié
     compté : −263 € enregistrables au Bar ROOM 1 du 03/10, contre +22 € sur
     la feuille, le rail et le bandeau (revue du 07/10/2026). */
  const lignes = useMemo<Rapprochement[]>(() => rapprochementsDeLaNuit(d).map((r) => {
    const attaches = d.discrepancies.filter((e) => e.closing_pos_id === (r.pos?.id ?? null))
    return { ...r, ecart: attaches.find((e) => e.status !== 'RESOLU') ?? attaches[0] ?? null }
  }), [d])

  /* ─── expliquer / accepter ─── */
  const [expl, setExpl] = useState<{
    id: string | null
    closingPosId: string | null
    titre: string
    source: number | null
    constate: number | null
    retenu: number | null
    montant: number | null
    code: string
    note: string
  } | null>(null)

  const ouvrirExplication = (r: Rapprochement) => {
    setExpl({
      id: r.ecart?.id ?? null,
      closingPosId: r.pos?.id ?? null,
      titre: r.titre,
      source: r.source, constate: r.constate, retenu: r.retenu,
      montant: r.ecart ? enCentimes(r.ecart.amount_cents) : r.delta,
      code: r.ecart?.reason_code ?? '',
      note: r.ecart?.note ?? '',
    })
  }

  /**
   * CRÉER PUIS QUALIFIER — deux écritures, une seule intention.
   *
   * La route de création n'accepte pas d'état : un écart naît toujours
   * « ouvert ». Et chaque écriture incrémente la version optimiste de la
   * clôture — la seconde doit donc partir de la version RENDUE par la
   * première, faute de quoi elle serait refusée par sa propre voisine.
   */
  const qualifier = async (o: {
    id: string | null
    closingPosId: string | null
    montant: number
    code: string | null
    note: string | null
    statut: DiscrepancyStatus
  }): Promise<boolean> => {
    let version = d.version
    let id = o.id
    setOccupe(true)
    try {
      if (id === null) {
        const c = await adminMutate<ReponseEcriture>('POST', '/api/v1/finance/discrepancies', {
          closing_id: d.id, closing_pos_id: o.closingPosId, amount_cents: o.montant,
          reason_code: o.code, note: o.note, expected_version: version,
        })
        if (c.status < 200 || c.status >= 300 || typeof c.data.id !== 'string') {
          toast('error', c.data.message ?? 'Cet écart n’a pas pu être enregistré.')
          return false
        }
        id = c.data.id
        if (typeof c.data.version === 'number') version = c.data.version
      }
      const r = await adminMutate<ReponseEcriture>('PATCH', `/api/v1/finance/discrepancies/${id}`, {
        closing_id: d.id,
        /* Le point de vente voyage aussi à la qualification. La route le
           retirait en silence, et l'écart du Bar ROOM 3 du 03/10, expliqué
           depuis sa ligne, tombait sur « toute la nuit » (08/10/2026) : elle le
           déclare désormais, et le cœur garde le sien quand il manque. */
        closing_pos_id: o.closingPosId,
        amount_cents: o.montant, reason_code: o.code, note: o.note,
        status: o.statut, expected_version: version,
      })
      if (r.status < 200 || r.status >= 300 || r.data.error !== undefined) {
        toast('error', r.data.message ?? 'L’état de cet écart n’a pas pu être enregistré.')
        return false
      }
      return true
    } finally {
      setOccupe(false)
      api.recharger()
    }
  }

  const enregistrerExplication = async (statut: 'EXPLIQUE' | 'ACCEPTE') => {
    if (!expl || expl.montant === null || !motifSuffisant(expl.note)) return
    const ok = await qualifier({
      id: expl.id, closingPosId: expl.closingPosId, montant: expl.montant,
      code: expl.code === '' ? null : expl.code, note: expl.note.trim(), statut,
    })
    if (ok) setExpl(null)
  }

  /* Solder ou rouvrir : aucun texte nouveau n'est exigé, mais l'explication
     déjà écrite est renvoyée telle quelle — le serveur réécrit la ligne
     entière, et ne rien lui transmettre effacerait le motif. */
  const changerEtat = async (e: DiscrepancyView, statut: DiscrepancyStatus) => {
    const montant = enCentimes(e.amount_cents)
    if (montant === null) return
    await qualifier({
      id: e.id, closingPosId: e.closing_pos_id, montant,
      code: e.reason_code, note: e.note, statut,
    })
  }

  /* Retirer une explication posée par erreur : en deux temps, sans boîte de
     dialogue du navigateur. Le delta, lui, ne bouge pas — la ligne redevient
     « non expliquée ». */
  const [aRetirer, setARetirer] = useState<string | null>(null)
  const retirer = async (e: DiscrepancyView) => {
    setOccupe(true)
    try {
      const r = await adminMutate<ReponseEcriture>('DELETE', `/api/v1/finance/discrepancies/${e.id}`, {
        closing_id: d.id, expected_version: d.version,
      })
      if (r.status < 200 || r.status >= 300 || r.data.error !== undefined) {
        toast('error', r.data.message ?? 'Cet écart n’a pas pu être retiré.')
      }
    } finally {
      setARetirer(null)
      setOccupe(false)
      api.recharger()
    }
  }

  /* ─── corriger le retenu, ligne par ligne ─── */
  const [retenue, setRetenue] = useState<{ pos: ClosingPosView; ligneId: string; cents: number | null; motif: string } | null>(null)
  const ligneRetenue = retenue ? retenue.pos.lines.find((l) => l.id === retenue.ligneId) ?? null : null
  const constateRetenue = ligneRetenue ? enCentimes(ligneRetenue.observed_cents) : null
  /* Confirmer le constaté n'est pas une correction : le motif n'est exigé que
     si le retenu s'en écarte. C'est exactement la règle du cœur. */
  const motifRequis = retenue !== null && retenue.cents !== constateRetenue

  const ouvrirRetenue = (p: ClosingPosView) => {
    const premiere = p.lines[0]
    if (!premiere) return
    setRetenue({
      pos: p, ligneId: premiere.id,
      cents: enCentimes(premiere.retained_cents) ?? enCentimes(premiere.observed_cents),
      motif: premiere.override_reason ?? '',
    })
  }

  const choisirLigne = (ligneId: string) => {
    setRetenue((r) => {
      if (!r) return r
      const l = r.pos.lines.find((x) => x.id === ligneId)
      if (!l) return r
      return {
        pos: r.pos, ligneId,
        cents: enCentimes(l.retained_cents) ?? enCentimes(l.observed_cents),
        motif: l.override_reason ?? '',
      }
    })
  }

  const poserRetenue = async () => {
    if (!retenue || retenue.cents === null) return
    if (motifRequis && !motifSuffisant(retenue.motif)) return
    setOccupe(true)
    const ok = await api.ecrire(
      'POST', `/api/v1/finance/closings/${d.id}/lines/${retenue.ligneId}/retain`,
      { retained_cents: retenue.cents, reason: retenue.motif.trim() },
    )
    setOccupe(false)
    if (ok) setRetenue(null)
  }

  /* ═══════════════════════ rendu ═══════════════════════ */

  /* L'état suit les SEUILS des réglages, comme le rail et les cases du point
     de vente : sous la vigilance, dans la tolérance ; au-delà de l'alerte, à
     expliquer. Un périmètre pas encore compté en entier n'a pas d'écart. */
  const etat = (r: Rapprochement): ReactNode => {
    if (r.ecart) {
      return (
        <span className={DISCREPANCY_STATUS_PILL[r.ecart.status] ?? 'lc-pill lc-pill--idle'}>
          {DISCREPANCY_STATUS_LABELS[r.ecart.status] ?? r.ecart.status}
        </span>
      )
    }
    if (r.delta === null) {
      if (r.etat === 'a-compter') return <span className="lc-hint">Pas encore compté</span>
      if (r.etat === 'partiel') {
        return <span className="lc-hint">{r.pos === null ? `Compté en entier : ${r.comptes} sur ${r.total}` : 'Compté en partie'}</span>
      }
      return <span className="lc-hint">Rien à confronter</span>
    }
    const niveau = niveauEcart(r.delta, seuils)
    if (r.delta === 0) return <span className="lc-pill lc-pill--ok">Aucun écart</span>
    if (niveau === 'ok') return <span className="lc-pill lc-pill--ok">Dans la tolérance</span>
    if (niveau === 'tiede') return <span className="lc-pill lc-pill--warning">À surveiller</span>
    return <span className="lc-pill lc-pill--error">Non expliqué</span>
  }

  return (
    <>
      {/* ═══════════ le rapprochement ═══════════ */}
      <section className="lc-panel">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><Scale size={17} /></span>
          <h2 className="lc-panel__title">Écarts de la nuit</h2>
        </div>
        <div className="lc-panel__body lc-panel__body--flush">
          <p className="lc-hint fiw-intro fiw-ecart__intro">
            Trois montants, jamais fondus en un seul : la SOURCE est ce que le
            fournisseur a annoncé, hors offerts, le CONSTATÉ ce que le terrain a
            compté — comptage d’espèces et terminaux compris —, le RETENU ce
            que la direction financière arrête. Le delta est la différence
            entre les deux premiers, les chiffres de la feuille de caisse ; il
            se lit une fois le point de vente compté en entier, ne se corrige
            pas, et s’explique.
          </p>

          <div className="fi-tablewrap">
            <table className="fi-table fiw-ecart-table">
              <thead>
                <tr>
                  <th scope="col">Périmètre</th>
                  <th scope="col" className="fi-money">Source</th>
                  <th scope="col" className="fi-money">Constaté</th>
                  <th scope="col" className="fi-money">Delta</th>
                  <th scope="col" className="fi-money">Retenu</th>
                  <th scope="col">État</th>
                  <th scope="col"><span className="fic-sr">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((r) => (
                  <tr key={r.pos?.id ?? 'nuit'} className={r.pos === null ? 'fiw-ecart__nuit' : undefined}>
                    <th scope="row">
                      {r.titre}
                      {r.lieu ? <span className="lc-hint">{r.lieu}</span> : null}
                    </th>
                    <td className="fi-money"><Montant cents={r.source} faible /></td>
                    <td className="fi-money"><Montant cents={r.constate} /></td>
                    <td className="fi-money"><EcartSeuil cents={r.delta} seuils={seuils} /></td>
                    <td className="fi-money"><Montant cents={r.retenu} fort /></td>
                    <td>{etat(r)}</td>
                    <td className="fic-cta">
                      {peutQualifier ? (
                        <button className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe}
                          onClick={() => ouvrirExplication(r)}>
                          {r.ecart ? 'Reprendre l’écart' : 'Expliquer l’écart'}
                        </button>
                      ) : null}
                      {peutRetenir && r.pos && r.pos.lines.length > 0 ? (
                        <button className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe}
                          onClick={() => ouvrirRetenue(r.pos!)}>Corriger le retenu</button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lc-panel__body">
          <p className="fi-reserve">
            Un terminal de paiement circule d’une caisse à l’autre au cours
            d’une nuit. Un montant carte qui ne retombe pas sur un terminal
            donné n’est donc pas une anomalie de caisse : c’est le
            fonctionnement normal. Le rapprochement carte se lit au point de
            vente, au lieu ou à la nuit — jamais terminal contre caisse.
          </p>
          {/* Un point de vente sans réponse n'est PAS fermé : il ne figure pas
              au rapprochement, et taire ce trou reviendrait à présenter un
              total complet qui ne l'est pas. */}
          {sansReponse > 0 ? (
            <p className="fi-provisoire">
              {sansReponse} point{sansReponse > 1 ? 's' : ''} de vente
              {sansReponse > 1 ? ' n’ont' : ' n’a'} pas encore reçu de réponse et
              {sansReponse > 1 ? ' ne figurent' : ' ne figure'} donc pas
              ci-dessus. Sans réponse ne veut pas dire fermé.{' '}
              <button className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => onEtape('nuit')}>
                Revenir au périmètre
              </button>
            </p>
          ) : null}
          {!peutQualifier && !figee ? (
            <p className="lc-hint">Votre rôle ne permet pas de qualifier un écart.</p>
          ) : null}
        </div>
      </section>

      {/* ═══════════ les écarts déjà enregistrés ═══════════ */}
      <section className="lc-panel">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><AlertTriangle size={17} /></span>
          <h2 className="lc-panel__title">Écarts enregistrés</h2>
          <span className="lc-panel__spacer" />
          <span className="lc-hint">
            {d.discrepancies.length === 0
              ? 'aucun'
              : `${d.discrepancies.filter((e) => e.status === 'OUVERT').length} ouvert(s) sur ${d.discrepancies.length}`}
          </span>
        </div>
        <div className="lc-panel__body">
          {d.discrepancies.length === 0 ? (
            <p className="fi-empty">
              Aucun écart n’a encore été relevé sur cette nuit. Un écart nul se
              lit « 0,00 € » dans le tableau ci-dessus : il n’a rien à
              enregistrer ici.
            </p>
          ) : (
            <ul className="fiw-ecarts">
              {d.discrepancies.map((e) => (
                <li key={e.id}>
                  <span className="fiw-ecarts__pos">{e.point_of_sale_name ?? 'Toute la nuit'}</span>
                  <Ecart cents={e.amount_cents} seuils={seuils} />
                  <span className={DISCREPANCY_STATUS_PILL[e.status] ?? 'lc-pill lc-pill--idle'}>
                    {DISCREPANCY_STATUS_LABELS[e.status] ?? e.status}
                  </span>
                  {e.reason_label ? <span className="lc-hint">{e.reason_label}</span> : null}
                  {e.note ? <span className="fi-reserve">« {e.note} »</span> : null}
                  {e.resolved_by_name ? (
                    <span className="lc-hint">{e.resolved_by_name} · {ilYA(e.resolved_at)}</span>
                  ) : null}
                  {peutQualifier ? (
                    <span className="fiw-ecarts__actions">
                      {e.status === 'RESOLU' ? (
                        <button className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe}
                          onClick={() => void changerEtat(e, 'OUVERT')}>Rouvrir</button>
                      ) : (
                        <button className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe}
                          onClick={() => void changerEtat(e, 'RESOLU')}>Solder</button>
                      )}
                      {aRetirer === e.id ? (
                        <>
                          <button className="lc-btn lc-btn--danger lc-btn--sm" disabled={occupe}
                            onClick={() => void retirer(e)}>Confirmer le retrait</button>
                          <button className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe}
                            onClick={() => setARetirer(null)}>Garder</button>
                        </>
                      ) : (
                        <button className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe}
                          onClick={() => setARetirer(e.id)}>Retirer</button>
                      )}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <p className="lc-hint">
            Un écart est un fait comptable à expliquer. Il n’est jamais
            transformé en note, en classement ou en sanction pour quelqu’un.
          </p>
          {d.discrepancies.length > 0 ? (
            <p className="lc-hint">
              Retirer un écart n’efface que son explication : la ligne redevient
              « non expliquée » et son delta ne change pas. Le retrait reste
              dans le journal.
            </p>
          ) : null}
          <button className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => onEtape('synthese')}>
            Aller à la synthèse
          </button>
        </div>
      </section>

      {/* ═══════════ expliquer un écart ═══════════ */}
      <Modal open={expl !== null} onClose={() => setExpl(null)} ariaLabel="Expliquer un écart" width={580} owning>
        {expl ? (
          <>
            <header className="lc-modal__head">
              <span className="lc-modal__icon" aria-hidden="true"><AlertTriangle size={22} /></span>
              <div>
                <div className="lc-modal__title">Expliquer un écart</div>
                <div className="lc-modal__pills">
                  <span className="lc-pill lc-pill--idle">{expl.titre}</span>
                </div>
              </div>
              <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close"
                onClick={() => setExpl(null)} aria-label="Fermer">
                <X size={18} />
              </button>
            </header>
            <div className="lc-modal__body fiw-modal">
              <div className="fi-triptyque">
                <div className="fi-triptyque__cell">
                  <div className="fi-triptyque__label">Source</div>
                  <div className="fi-triptyque__value">{formatMoney(expl.source)}</div>
                </div>
                <div className="fi-triptyque__cell">
                  <div className="fi-triptyque__label">Constaté</div>
                  <div className="fi-triptyque__value">{formatMoney(expl.constate)}</div>
                </div>
                <div className="fi-triptyque__cell fi-triptyque__cell--retenu">
                  <div className="fi-triptyque__label">Retenu</div>
                  <div className="fi-triptyque__value">{formatMoney(expl.retenu)}</div>
                </div>
              </div>

              <div className="lc-field">
                <span className="lc-label">Montant de l’écart</span>
                <MoneyInput
                  cents={expl.montant}
                  onChange={(c) => setExpl((s) => (s ? { ...s, montant: c } : s))}
                  ariaLabel="Montant de l’écart"
                />
                <span className="lc-helper">
                  Négatif, il manque ; positif, il y a plus que prévu. Les deux
                  existent et les deux s’expliquent.
                </span>
              </div>

              {/* Deux écarts du même montant sur la même nuit, c'est souvent le
                  même écart saisi deux fois. On le SIGNALE ; on ne les
                  rapproche pas tout seul — « ça ressemble à » n'est pas une
                  réponse en comptabilité. */}
              {expl.id === null && expl.montant !== null
                && d.discrepancies.some((e) => enCentimes(e.amount_cents) === expl.montant) ? (
                <p className="fi-provisoire">
                  Un écart du même montant est déjà enregistré sur cette nuit.
                  Vérifiez qu’il ne s’agit pas du même : rien ne les rapproche
                  automatiquement.
                </p>
              ) : null}

              <div className="lc-field">
                <span className="lc-label">Motif</span>
                <Select
                  value={expl.code}
                  options={motifs.filter((m) => m.active).map((m) => ({ value: m.code, label: m.label }))}
                  onChange={(v) => setExpl((s) => (s ? { ...s, code: v } : s))}
                  placeholder="Choisir un motif"
                  ariaLabel="Motif de l’écart"
                />
                {motifs.length === 0 ? (
                  <span className="lc-helper">
                    Les motifs n’ont pas pu être lus : l’explication ci-dessous
                    reste enregistrée telle quelle.
                  </span>
                ) : null}
              </div>

              <MotifField
                id="fiw-motif-ecart"
                valeur={expl.note}
                onChange={(v) => setExpl((s) => (s ? { ...s, note: v } : s))}
                label="Ce qui s’est passé"
                aide="Accepter un écart sans phrase, c’est le perdre : cette explication reste attachée à la nuit."
              />

              <div className="fiw-modal__actions">
                <button className="lc-btn lc-btn--ghost" onClick={() => setExpl(null)}>Annuler</button>
                <button
                  className="lc-btn"
                  disabled={occupe || expl.montant === null || !motifSuffisant(expl.note)}
                  onClick={() => void enregistrerExplication('ACCEPTE')}
                >Accepter cet écart</button>
                <button
                  className="lc-btn lc-btn--primary"
                  disabled={occupe || expl.montant === null || !motifSuffisant(expl.note)}
                  onClick={() => void enregistrerExplication('EXPLIQUE')}
                >Enregistrer l’explication</button>
              </div>
            </div>
          </>
        ) : null}
      </Modal>

      {/* ═══════════ corriger le retenu ═══════════ */}
      <Modal open={retenue !== null} onClose={() => setRetenue(null)} ariaLabel="Corriger le montant retenu" width={580} owning>
        {retenue && ligneRetenue ? (
          <>
            <header className="lc-modal__head">
              <span className="lc-modal__icon" aria-hidden="true"><Scale size={22} /></span>
              <div>
                <div className="lc-modal__title">Corriger le montant retenu</div>
                <div className="lc-modal__pills">
                  <span className="lc-pill lc-pill--idle">{retenue.pos.point_of_sale_name}</span>
                </div>
              </div>
              <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close"
                onClick={() => setRetenue(null)} aria-label="Fermer">
                <X size={18} />
              </button>
            </header>
            <div className="lc-modal__body fiw-modal">
              <div className="lc-field">
                <span className="lc-label">Sur quel moyen de paiement ?</span>
                <Select
                  value={retenue.ligneId}
                  options={retenue.pos.lines.map((l) => ({
                    value: l.id,
                    label: `${l.payment_method_label}${l.register_name ? ` · ${l.register_name}` : ''}`,
                  }))}
                  onChange={choisirLigne}
                  ariaLabel="Ligne à corriger"
                />
              </div>

              <div className="fi-triptyque">
                <div className="fi-triptyque__cell">
                  <div className="fi-triptyque__label">Source</div>
                  <div className="fi-triptyque__value">{formatMoney(ligneRetenue.source_cents)}</div>
                </div>
                <div className="fi-triptyque__cell">
                  <div className="fi-triptyque__label">Constaté</div>
                  <div className="fi-triptyque__value">{formatMoney(ligneRetenue.observed_cents)}</div>
                </div>
                <div className="fi-triptyque__cell fi-triptyque__cell--retenu">
                  <div className="fi-triptyque__label">Retenu</div>
                  <div className="fi-triptyque__value">{formatMoney(retenue.cents)}</div>
                </div>
              </div>

              <p className="lc-hint">
                La source ne disparaît jamais. Retenir un autre montant ajoute
                une décision par-dessus, sans effacer ce que le fournisseur a
                transmis ni ce que le terrain a compté.
              </p>

              <div className="lc-field">
                <span className="lc-label">Montant retenu</span>
                <MoneyInput
                  cents={retenue.cents}
                  onChange={(c) => setRetenue((r) => (r ? { ...r, cents: c } : r))}
                  ariaLabel="Montant retenu"
                />
              </div>

              {motifRequis ? (
                <MotifField
                  id="fiw-motif-retenu-ecart"
                  valeur={retenue.motif}
                  onChange={(v) => setRetenue((r) => (r ? { ...r, motif: v } : r))}
                  aide="Retenir un montant différent du constaté est une décision : elle se motive."
                />
              ) : (
                <p className="lc-hint">
                  Le retenu est égal au constaté : rien à motiver. Une phrase
                  imposée là où il n’y a pas de décision ne produit que des
                  « RAS ».
                </p>
              )}

              <div className="fiw-modal__actions">
                <button className="lc-btn lc-btn--ghost" onClick={() => setRetenue(null)}>Annuler</button>
                <button
                  className="lc-btn lc-btn--primary"
                  disabled={occupe || retenue.cents === null || (motifRequis && !motifSuffisant(retenue.motif))}
                  onClick={() => void poserRetenue()}
                >Retenir ce montant</button>
              </div>
            </div>
          </>
        ) : null}
      </Modal>
    </>
  )
}

/* ═══════════════════════════ ÉTAPE « SYNTHÈSE » ═══════════════════════════ */

export function ClosingStepSummary(props: {
  api: ClosingApi
  /** Les pièces d'argent de la nuit (rapports ZAP, redditions) — pas les annexes. */
  pieces: DocVisible[]
  figee: boolean
  onEtape: (cle: string) => void
  /**
   * La fiche de la nuit, déjà lue par l'assistant : elle porte les préventes
   * du total de la nuit. Absente, l'étape la lit elle-même.
   */
  nuit?: Etat<NightDetailResponse>
}) {
  const { api, pieces, figee, onEtape } = props
  const d = api.closing
  const toast = useToast()
  const seuils = { warn: api.settings.discrepancy_warn_cents, alert: api.settings.discrepancy_alert_cents }

  const [revision, setRevision] = useState<{ ouvert: boolean; motif: string }>({ ouvert: false, motif: '' })
  const [occupe, setOccupe] = useState(false)

  const ouverts = d.pos.filter((p) => p.was_open === true)
  const sansReponse = d.pos.filter((p) => p.was_open === null)
  /* Une vente du guichet d'une autre nuit décidée APRÈS la finalisation : la
     clôture reste intacte, et dit qu'une révision est conseillée (08/10/2026). */
  const conseils = (d.off_night?.cases ?? []).filter((c) => c.revision !== null && c.revision.business_date === d.business_date)
  // Le serveur sert la ventilation (guichet, préventes…) en plus des cinq
  // champs du contrat : la liste l'écrit quand elle est là.
  const entrees: EntreesNuit | null = d.entries ?? null

  /* ─── le total de la nuit, façon classeur de caisse ─── */
  // Les préventes viennent de la fiche de nuit : celle de l'assistant quand il
  // la prête, sinon une lecture ici, relue à chaque version de la clôture.
  const nuitLue = useNuitFinance(props.nuit ? null : d.business_date, d.version)
  const nuit: NuitAvecPreventes | null = props.nuit
    ? (props.nuit.etat === 'ok' ? (props.nuit.valeur as NuitAvecPreventes) : null)
    : nuitLue
  const feuille = useMemo(() => feuilleDeCaisse(d, nuit), [d, nuit])
  // Le chiffre que la finalisation arrête : sur place + préventes reversées (11/10/2026).
  const officiel = chiffreOfficiel(d)
  const ventilation = ventilationDuChiffre(officiel)

  /* Déclarer prête et finaliser ne vivent plus ici : le geste est dans le pied
     de l'assistant (`useGesteCloture`, en fin de fichier ; `DecisionCloture`). */

  /* ─── corriger : une révision, jamais une réécriture ─── */
  const creerRevision = async () => {
    if (!motifSuffisant(revision.motif)) return
    setOccupe(true)
    const r = await adminMutate<{ error?: string; id?: string; revision?: number; message?: string }>(
      'POST', `/api/v1/finance/closings/${d.id}/revise`, { reason: revision.motif.trim() },
    )
    setOccupe(false)
    if (r.status >= 200 && r.status < 300 && typeof r.data.id === 'string') {
      setRevision({ ouvert: false, motif: '' })
      toast('ok', 'Révision créée. La version précédente reste lisible telle quelle.')
      // REMPLACER l'adresse, pas en empiler une : la clôture finalisée devient
      // une révision remplacée, et « Retour » ramènerait sur elle au lieu de
      // revenir là d'où l'on venait avant d'ouvrir la clôture.
      redirect(`/comptabilite/clotures/${r.data.id}/nuit`)
      return
    }
    toast('error', r.data.message ?? 'La révision n’a pas pu être créée.')
  }

  return (
    <>
      {/* ═══════════ le total de la nuit ═══════════
          « Un vrai total avec écart total », comme le classeur de caisse :
          compté contre rapport, point de vente par point de vente, hors
          offerts ; puis le chiffre d'affaires et le récap des espèces. */}
      <FeuilleDeCaisse
        feuille={feuille}
        seuils={seuils}
        onPointDeVente={onEtape}
        offertsVisibles
        lienBanque={api.peut('finance.bank.read')}
      />

      {/* ═══════════ ce qui sera arrêté ═══════════
          Le RETENU, troisième montant de la doctrine : ce que la finalisation
          fige. Par moyen de paiement, puis par point de vente. */}
      <section className="lc-panel">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><Lock size={17} /></span>
          <h2 className="lc-panel__title">Ce qui sera arrêté</h2>
          <span className="lc-panel__spacer" />
          <span className="lc-hint">{ouverts.length} point{ouverts.length > 1 ? 's' : ''} de vente ouvert{ouverts.length > 1 ? 's' : ''} cette nuit-là</span>
        </div>
        <div className="lc-panel__body">
          <div className="ffc-arrete">
            {officiel.servi ? (
              <div className="ffc-retenu">
                <span className="ffc-retenu__libelle">{figee ? 'Chiffre officiel arrêté' : 'Chiffre officiel de la nuit'}</span>
                <span className="ffc-retenu__valeur">{formatMoney(officiel.total)}</span>
                {ventilation !== null ? <p className="lc-hint">{ventilation}{officiel.provenance ? ` (${officiel.provenance})` : ''}</p> : null}
              </div>
            ) : null}
            <div className={officiel.servi ? 'ffc-retenu ffc-retenu--lignes' : 'ffc-retenu'}>
              <span className="ffc-retenu__libelle">Retenu des lignes</span>
              <span className="ffc-retenu__valeur">{formatMoney(d.totals.retained_cents)}</span>
              <p className="lc-hint">Ce que la finalisation arrêtera, ligne par ligne : le constaté, sauf correction motivée ; à défaut, le rapport. Les offerts y figurent, rangés à part : ils n’entrent pas dans le chiffre officiel de la nuit.</p>
              {/* Le retenu ne lit que les LIGNES : des espèces comptées non
                  reportées l'écartent du total compté de la feuille. Dit ici,
                  au lieu d'un écart muet entre deux chiffres de la page. */}
              {!figee && feuille.aReporter > 0 ? (
                <p className="fi-provisoire">
                  {feuille.aReporter > 1 ? `${feuille.aReporter} points de vente ont` : 'Un point de vente a'} des espèces
                  comptées qu’aucune ligne Espèces ne porte encore : le retenu ne les connaît pas, et la nuit ne se
                  finalise pas avant leur report.
                </p>
              ) : null}
            </div>

            {d.totals.by_payment_method.length > 0 ? (
              <div className="fi-tablewrap">
                <table className="fi-table">
                  <thead>
                    <tr>
                      <th scope="col">Moyen de paiement</th>
                      <th scope="col" className="fi-money">Retenu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.totals.by_payment_method.map((m) => (
                      <tr key={m.payment_method}>
                        <th scope="row">{m.label}</th>
                        <td className="fi-money"><Montant cents={m.retained_cents} /></td>
                      </tr>
                    ))}
                    <tr>
                      <th scope="row">dont espèces</th>
                      <td className="fi-money"><Montant cents={d.totals.cash_cents} /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : null}

            <div className="fi-tablewrap">
              <table className="fi-table">
                <thead>
                  <tr>
                    <th scope="col">Point de vente</th>
                    <th scope="col">Périmètre</th>
                    <th scope="col" className="fi-money">Retenu</th>
                    <th scope="col"><span className="fic-sr">Étape</span></th>
                  </tr>
                </thead>
                <tbody>
                  {d.pos.map((p) => (
                    <tr key={p.id}>
                      <th scope="row">
                        {p.point_of_sale_name}
                        <span className="lc-hint">{p.location_name}</span>
                      </th>
                      <td>
                        {p.was_open === null
                          ? <span className="lc-pill lc-pill--warning">Sans réponse</span>
                          : p.was_open
                            ? <span className="lc-pill lc-pill--ok">Ouvert</span>
                            : <span className="lc-pill lc-pill--idle">Fermé</span>}
                      </td>
                      <td className="fi-money">{p.was_open ? <Montant cents={p.totals.retained_cents} fort /> : '—'}</td>
                      <td className="fic-cta">
                        {p.was_open === true ? (
                          <button className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => onEtape(p.id)}>Ouvrir</button>
                        ) : p.was_open === null ? (
                          <button className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => onEtape('nuit')}>Répondre</button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="fi-reserve">
            Le chiffre officiel compte ce que la nuit a encaissé sur place, Accueil compris, et ses préventes
            en ligne pour ce que Shotgun reverse — d’après la reddition ou, à défaut, les billets (prix + 1 € de
            frais rendu). La finalisation fige les deux : une reddition reçue après ne change plus ce chiffre,
            seule une révision le peut.
          </p>
        </div>
      </section>

      {/* ═══════════ finaliser ═══════════
          Juste après « Ce qui sera arrêté » : ce que la finalisation fige, puis
          ce qu'elle veut dire et ce qui l'empêche encore. Le GESTE, lui, est
          dans le pied de l'assistant, visible à toute hauteur de page : un seul
          bouton principal, à un seul endroit (11/10/2026). */}
      <section className="lc-panel fiw-final">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true">
            {figee ? <Lock size={17} /> : <CheckCircle2 size={17} />}
          </span>
          <h2 className="lc-panel__title">{figee ? 'Nuit arrêtée' : 'Finaliser la nuit'}</h2>
        </div>
        <div className="lc-panel__body">
          {!figee ? (
            <p className="lc-hint">
              {officiel.servi && officiel.total !== null
                ? `Finaliser arrête le chiffre officiel de la nuit (${formatMoney(officiel.total)}) : le sur-place retenu et les préventes reversées par Shotgun. `
                : officiel.servi
                  ? 'Finaliser arrête le chiffre officiel de la nuit : le sur-place retenu et les préventes reversées par Shotgun. '
                  : `Finaliser arrête le retenu (${formatMoney(d.totals.retained_cents)}) : il devient le chiffre officiel de la nuit. `}
              Ensuite plus rien ne se modifie, corriger demandera une révision.
            </p>
          ) : null}

          {/* La validation en bloc que la finalisation remplacerait : la
              conséquence AVANT le geste, jamais découverte au refus. */}
          {!figee && d.validation_replacement ? <ValidationRemplacee r={d.validation_replacement} /> : null}
          {/* Et une fois finalisée (ou en révision) : ce que la finalisation
              a remplacé, et le motif — la nuit ne l'oublie pas. */}
          {!d.validation_replacement && d.replaced_validation ? <TraceRemplacement t={d.replaced_validation} /> : null}

          {d.blockers.length > 0 && !figee ? (
            <div className="fiw-blockers" id="fiw-bloquants" tabIndex={-1}>
              <p className="fi-kpi__label">Ce qui empêche encore de finaliser</p>
              <ul>{d.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
            </div>
          ) : null}

          {sansReponse.length > 0 && !figee ? (
            <p className="fi-provisoire">
              {sansReponse.length} point{sansReponse.length > 1 ? 's' : ''} de vente
              {sansReponse.length > 1 ? ' n’ont' : ' n’a'} pas encore reçu de réponse.
              Sans réponse ne veut pas dire fermé.
            </p>
          ) : null}

          {!figee && ((d.status === 'READY' && api.peut('finance.closure.close')) || (d.status === 'DRAFT' && api.peut('finance.closure.edit'))) ? (
            <p className="lc-hint ffin-renvoi">
              <ArrowDown size={15} aria-hidden="true" />Le geste est dans la barre en bas de l’écran.
            </p>
          ) : null}

          {figee && d.status === 'CLOSED' && conseils.length > 0 ? (
            <div className="fiw-blockers">
              <p className="fi-kpi__label">Révision conseillée</p>
              <ul>
                {conseils.map((c) => (
                  <li key={c.key}>
                    {c.sentence} {c.revision!.message}
                    {c.revision!.ca_delta_cents !== null && c.revision!.ca_delta_cents !== 0
                      ? ` Effet sur le chiffre d’affaires : ${formatEcart(c.revision!.ca_delta_cents).texte}.`
                      : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {figee ? (
            <>
              <p className="lc-hint">
                {d.status === 'CLOSED'
                  ? `Finalisée ${ilYA(d.closed_at)}${d.closed_by_name ? ` par ${d.closed_by_name}` : ''}.`
                  : 'Cette révision a été remplacée. Elle reste lisible telle qu’elle a été arrêtée.'}
                {d.revision_reason ? ` Motif de la révision : « ${d.revision_reason} »` : ''}
              </p>
              {d.components.length > 0 ? (
                <div className="fi-tablewrap fiw-composantes">
                  <table className="fi-table">
                    <thead>
                      <tr>
                        <th scope="col">Composante de recette</th>
                        <th scope="col" className="fi-money">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.components.map((c) => (
                        <tr key={`${c.component_code}-${c.point_of_sale_id ?? 'nuit'}`}>
                          <th scope="row">{c.component_code === 'TICKETING_ONLINE' ? 'Préventes reversées par Shotgun' : c.label}</th>
                          <td className="fi-money"><Montant cents={c.amount_cents} /></td>
                        </tr>
                      ))}
                      {officiel.servi && officiel.figees && officiel.total !== null ? (
                        <tr>
                          <th scope="row">Chiffre officiel, hors offerts</th>
                          <td className="fi-money"><Montant cents={officiel.total} fort /></td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {d.status === 'CLOSED' && api.peut('finance.closure.revise') ? (
                <button className="lc-btn" onClick={() => setRevision({ ouvert: true, motif: '' })}>
                  <RotateCcw size={16} aria-hidden="true" />Corriger : créer une révision
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      {/* ═══════════ les entrées ═══════════
          Des personnes, pas des euros : billets Shotgun relevés, guestlist
          saisie à l'étape « Nuit ». La finalisation les fige avec le chiffre. */}
      <section className="lc-panel">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><Users size={17} /></span>
          <h2 className="lc-panel__title">Entrées de la nuit</h2>
        </div>
        <div className="lc-panel__body">
          {entrees === null ? (
            <p className="fi-empty">Les entrées de cette nuit ne sont pas encore servies par le serveur.</p>
          ) : (
            <>
              <ListeEntrees e={entrees} />
              <p className="lc-hint fiw-note">
                {entrees.frozen_at
                  ? 'Figées à la finalisation : un billet remboursé ou relu après ne change plus ce nombre.'
                  : 'Les billets sont relus dans Shotgun jusqu’à la finalisation, qui fige le total avec le chiffre de la nuit.'}
              </p>
              {entrees.guestlist_count === null && !figee && api.peut('finance.closure.edit') ? (
                <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => onEtape('nuit')}>
                  Saisir la guestlist
                </button>
              ) : null}
            </>
          )}
        </div>
      </section>

      {/* ═══════════ la billetterie des associations (Pretix), 16/10/2026 ═══════════
          En direct jusqu'à la finalisation, figée par elle ; jamais bloquante. */}
      {/* Finalisée avant le gel (avant le 16/10/2026) : rien n'a été arrêté pour elle. */}
      <BilletterieAssociations
        a={d.associations}
        peutRegler={api.peut('finance.reference.read')}
        peutVoirSoirees={api.peut('soiree.read')}
        situation={(d.status === 'CLOSED' || d.status === 'SUPERSEDED') && d.associations?.frozen !== true ? 'FINALISEE_SANS' : null}
      />

      {/* ═══════════ écarts ═══════════ */}
      <section className="lc-panel">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><AlertTriangle size={17} /></span>
          <h2 className="lc-panel__title">Écarts</h2>
        </div>
        <div className="lc-panel__body">
          {d.discrepancies.length === 0 ? (
            <p className="fi-empty">Aucun écart n’a été relevé sur cette nuit.</p>
          ) : (
            <ul className="fiw-ecarts">
              {d.discrepancies.map((e) => (
                <li key={e.id}>
                  <span className="fiw-ecarts__pos">{e.point_of_sale_name ?? 'Toute la nuit'}</span>
                  <Ecart cents={e.amount_cents} seuils={seuils} />
                  <span className={e.status === 'OUVERT' ? 'lc-pill lc-pill--warning' : 'lc-pill lc-pill--idle'}>
                    {DISCREPANCY_STATUS_LABELS[e.status] ?? e.status}
                  </span>
                  {e.reason_label ? <span className="lc-hint">{e.reason_label}</span> : null}
                  {e.note ? <span className="fi-reserve">« {e.note} »</span> : null}
                </li>
              ))}
            </ul>
          )}
          <p className="lc-hint">
            Un écart est un fait comptable à expliquer. Il n’est jamais
            transformé en note, en classement ou en sanction pour quelqu’un.
          </p>
          {/* La synthèse RÉCAPITULE ; c'est l'étape « Écarts » qui agit. Deux
              formulaires pour le même geste auraient fini par diverger. */}
          <button className="lc-btn lc-btn--sm" onClick={() => onEtape('ecarts')}>
            {d.discrepancies.length === 0 ? 'Voir le rapprochement de la nuit' : 'Expliquer, accepter, corriger'}
          </button>
        </div>
      </section>

      {/* ═══════════ sources de la nuit ═══════════ */}
      {pieces.length > 0 ? (
        <section className="lc-panel">
          <div className="lc-panel__head"><h2 className="lc-panel__title">Pièces d’argent de la nuit</h2></div>
          <div className="lc-panel__body">
            <ul className="fiw-pieces__liste">
              {pieces.map((p) => (
                <li key={p.id}>
                  <VoirSource doc={p} liste={pieces} className="fiw-piece">
                    <span className="fiw-piece__lib">{p.libelle ?? p.filename}</span>
                  </VoirSource>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      {/* ═══════════ créer une révision ═══════════ */}
      <Modal open={revision.ouvert} onClose={() => setRevision({ ouvert: false, motif: '' })} ariaLabel="Créer une révision" width={540} owning>
        <header className="lc-modal__head">
          <span className="lc-modal__icon" aria-hidden="true"><RotateCcw size={22} /></span>
          <div>
            <div className="lc-modal__title">Créer une révision</div>
            <div className="lc-modal__pills">
              <span className="lc-pill lc-pill--idle">{nuitLongue(d.business_date)}</span>
            </div>
          </div>
          <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close"
            onClick={() => setRevision({ ouvert: false, motif: '' })} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>
        <div className="lc-modal__body fiw-modal">
          <p className="lc-hint">
            La révision part de cette clôture, à l’identique. Vous corrigez
            ensuite ce qui doit l’être : la version actuelle reste intacte et
            reconstructible, avec son écart conservé.
          </p>
          <MotifField
            id="fiw-motif-revision"
            valeur={revision.motif}
            onChange={(v) => setRevision((r) => ({ ...r, motif: v }))}
            label="Pourquoi cette nuit doit-elle être corrigée ?"
          />
          <div className="fiw-modal__actions">
            <button className="lc-btn lc-btn--ghost" onClick={() => setRevision({ ouvert: false, motif: '' })}>Annuler</button>
            <button className="lc-btn lc-btn--primary" disabled={occupe || !motifSuffisant(revision.motif)}
              onClick={() => void creerRevision()}>
              Créer la révision
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

/* ═══════════════════════════ LE GESTE DE CLÔTURE ═══════════════════════════ */

/**
 * Ce que le pied de l'assistant tient en main : déclarer prête, finaliser,
 * et l'élément d'où part le sceau. Le bouton vit dans `DecisionCloture` ;
 * la logique, la confirmation et la route restent ICI, à côté de ce que la
 * synthèse montre de la nuit qu'on arrête.
 */
export interface GesteCloture {
  occupe: boolean
  marquerPrete: () => Promise<void>
  /** Rend vrai quand la nuit est finalisée. */
  finaliser: (motif?: string) => Promise<boolean>
  /**
   * La confirmation MOTIVÉE du remplacement d'une validation plus haute
   * (16/10/2026) : ouverte par `finaliser` quand le serveur l'exige, rendue
   * par `DecisionCloture`. Le motif part avec la finalisation, dans l'audit.
   */
  remplacement: { ouvert: boolean; motif: string }
  setMotifRemplacement: (motif: string) => void
  fermerRemplacement: () => void
  finaliserMalgreLaBaisse: () => Promise<void>
  /** L'emplacement du geste, dans le pied : le sceau en part. */
  ctaRef: RefObject<HTMLSpanElement | null>
}

/**
 * Appelé par l'assistant AVANT ses retours anticipés (`api` est alors `null`
 * tant que la clôture n'est pas lue) : un hook ne se saute pas.
 *
 * `apercu` (QA, `?apercu=finalisation`) rejoue le sceau d'une nuit DÉJÀ
 * finalisée, pour qui a le droit de finaliser : aucune requête d'écriture,
 * et le toast le dit.
 */
export function useGesteCloture(api: ClosingApi | null, opts: { apercu: boolean }): GesteCloture {
  const toast = useToast()
  const confirmer = useConfirm()
  const [occupe, setOccupe] = useState(false)
  const [remplacement, setRemplacement] = useState({ ouvert: false, motif: '' })
  const ctaRef = useRef<HTMLSpanElement>(null)
  // Le sceau ne se joue que si l'assistant est encore là quand la réponse
  // arrive : on ne détourne jamais quelqu'un qui est parti ailleurs.
  const vivant = useRef(true)
  useEffect(() => {
    vivant.current = true
    return () => { vivant.current = false }
  }, [])

  /* ─── prête à finaliser ─── */
  const marquerPrete = useCallback(async () => {
    if (!api) return
    const d = api.closing
    setOccupe(true)
    await api.ecrire('POST', `/api/v1/finance/closings/${d.id}/ready`, {})
    setOccupe(false)
  }, [api])

  /* ─── finaliser ─── */
  // `motif` : la confirmation MOTIVÉE du remplacement d'une validation plus
  // haute (16/10/2026), donnée dans la fenêtre de `DecisionCloture`. Sans
  // elle, une nuit qui l'exige ouvre cette fenêtre au lieu de la confirmation.
  const finaliser = useCallback(async (motif?: string): Promise<boolean> => {
    if (!api) return false
    const d = api.closing
    // Un obstacle réapparu après « prête » (préventes plus connues, synchro) :
    // le dire et y mener, plutôt que de confirmer un geste que le serveur refusera.
    if (d.blockers.length > 0) {
      toast('error', `La nuit ne peut pas encore être finalisée : ${d.blockers[0]}`)
      document.getElementById('fiw-bloquants')?.focus()
      return false
    }
    // LE TOTAL, pas le seul sur-place (11/10/2026) : sur place + préventes
    // reversées par Shotgun, ventilé. Le retenu des lignes ne sert plus que
    // pour un serveur d'avant ce contrat.
    const officiel = chiffreOfficiel(d)
    if (motif === undefined) {
      // UNE VALIDATION PLUS HAUTE SERAIT REMPLACÉE : la confirmation simple ne
      // suffit pas, le serveur exige un motif — la fenêtre motivée la remplace.
      // Le motif déjà tapé est GARDÉ : une finalisation refusée (vérification
      // renforcée annulée, total qui a bougé) ne le fait pas retaper.
      if (d.validation_replacement?.requires_confirmation) {
        setRemplacement((x) => ({ ouvert: true, motif: x.motif }))
        return false
      }
      const { message, details } = recapFinalisation(d)
      const ok = await confirmer({
        title: 'Finaliser la nuit',
        message,
        details: [
          ...details,
          // Sous le seuil, le remplacement se dit sans exiger de motif.
          ...(d.validation_replacement ? [{ label: 'Validation remplacée', value: d.validation_replacement.sentence }] : []),
        ],
        variant: 'critical',
        confirmLabel: 'Finaliser',
      })
      if (!ok) return false
    }
    setOccupe(true)
    // La vérification renforcée, quand elle est due, a lieu DANS cet appel
    // (adminMutate la demande puis rejoue) : `passe` n'est vrai qu'après le 2xx.
    // Le total confirmé part avec : s'il a bougé depuis (une reddition lue
    // entre-temps), le serveur refuse au lieu d'arrêter un autre montant.
    //
    // 409 FINANCE_CONFIRMATION — la nuit a été validée plus haut (ou le seuil
    // abaissé) depuis l'affichage : ce n'est PAS un conflit de version, et
    // personne n'a touché la clôture. On rouvre la fenêtre motivée (la
    // relecture qui suit lui apporte la validation à remplacer) au lieu du
    // « quelqu'un d'autre a modifié cette clôture » des conflits.
    const passe = await api.ecrire('POST', `/api/v1/finance/closings/${d.id}/close`, {
      ...(officiel.total !== null ? { expected_total_cents: officiel.total } : {}),
      ...(motif !== undefined ? { replace_validation_reason: motif } : {}),
    }, (erreur) => {
      if (erreur !== 'FINANCE_CONFIRMATION') return false
      setRemplacement((x) => ({ ouvert: true, motif: x.motif }))
      return true
    })
    setOccupe(false)
    if (!passe) api.recharger()
    if (passe) {
      if (vivant.current && window.location.pathname.startsWith('/comptabilite/clotures/')) {
        sceller(d, ctaRef.current?.getBoundingClientRect() ?? null, false, toast)
      } else {
        toast('ok', ANNONCE_FINALISEE)
      }
    }
    return passe
  }, [api, confirmer, toast])

  const finaliserMalgreLaBaisse = useCallback(async () => {
    if (!motifSuffisant(remplacement.motif)) return
    const motif = remplacement.motif.trim()
    // La fenêtre se ferme pour laisser place à la vérification renforcée,
    // mais le motif reste : refusée, la finalisation le retrouvera tapé.
    setRemplacement((x) => ({ ...x, ouvert: false }))
    if (await finaliser(motif)) setRemplacement({ ouvert: false, motif: '' })
  }, [remplacement.motif, finaliser])
  const fermerRemplacement = useCallback(() => setRemplacement({ ouvert: false, motif: '' }), [])
  const setMotifRemplacement = useCallback((motif: string) => setRemplacement((r) => ({ ...r, motif })), [])

  /* ─── l'aperçu : une seule fois, sur une nuit déjà finalisée ─── */
  const joue = useRef(false)
  useEffect(() => {
    if (!opts.apercu || joue.current || !api) return
    joue.current = true
    if (api.closing.status === 'CLOSED' && api.peut('finance.closure.close')) {
      sceller(api.closing, ctaRef.current?.getBoundingClientRect() ?? null, true, toast)
    } else {
      toast('error', api.closing.status === 'CLOSED'
        ? 'Aperçu réservé aux rôles qui peuvent finaliser une nuit.'
        : 'Aperçu possible seulement sur une nuit finalisée.')
    }
  }, [opts.apercu, api, toast])

  return { occupe, marquerPrete, finaliser, remplacement, setMotifRemplacement, fermerRemplacement, finaliserMalgreLaBaisse, ctaRef }
}
